import { registerPlugin, WebPlugin } from '@capacitor/core';

import { desktopBruecke, istDesktop } from './gleis';
import { baueSchirmTeile, Schirmhalter, type SchirmTeile } from './screenshotWeb';

/**
 * Abzug des sichtbaren Bildschirms.
 *
 * Auf dem Gerät dahinter: `ios/App/App/ScreenshotPlugin.swift` mit
 * `WKWebView.takeSnapshot`. Das Plugin liefert nur Pixel — was aufgenommen wird und wohin es
 * kommt, entscheidet der Web-Teil (s. `features/bogen/bogenShot.ts`).
 */

/**
 * Wie stark komprimiert wird (0…1) und mit welchem Vielfachen der Punktgrösse aufgenommen
 * wird (1 = 1x, kein Retina-Faktor).
 *
 * **Vorgabe des Nutzers vom 2026-09-28:** JPEG statt PNG, 1x statt 2x — die Abzüge sind seit
 * dem Wechsel-Abzug die grösste Datenmenge der App. Ein 2x-PNG eines Bogens wiegt 0,4–1,5 MB
 * je Kachel, bis zu drei Kacheln je Abzug; so bleiben rund 150–250 KB. Grob Faktor 15.
 *
 * ⚠️ **0,85 und nicht 0,7.** Der Schritt auf 1x spart für sich schon drei Viertel der Pixel;
 * die höhere Qualität kostet davon wenig zurück und hält dünne Schrift sauber. JPEG ist bei
 * Text der ungünstigste Fall — harte Kanten, Chroma-Unterabtastung —, und bei 1x fehlt die
 * Reserve, die 2x bisher verschenkt hat.
 *
 * ⚠️ **Hier und nur hier.** Die Werte gehen als Angaben an die Erzeuger, auch über die
 * Capacitor-Brücke ins Swift-Plugin. Stünde 0,85 zusätzlich in Swift, wäre das die Stelle,
 * die beim nächsten Nachziehen vergessen wird.
 */
export const ABZUG_QUALITAET = 0.85;
export const ABZUG_SKALIERUNG = 1;

/** Angaben an den Erzeuger; fehlen sie, gelten die Konstanten oben. */
export interface ShotOptionen {
  qualitaet?: number;
  skalierung?: number;
}

export const abzugsOptionen = (ueber: ShotOptionen = {}): Required<ShotOptionen> => ({
  qualitaet: ueber.qualitaet ?? ABZUG_QUALITAET,
  skalierung: ueber.skalierung ?? ABZUG_SKALIERUNG,
});

export interface ShotResult {
  base64: string;
  width: number;
  height: number;
  /**
   * Was wirklich in den Bytes steht.
   *
   * ⚠️ **Gemeldet, nicht angenommen.** Schlägt das JPEG-Kodieren fehl, fällt der Erzeuger auf
   * PNG zurück — und sagt es. Eine `.jpg`-Datei mit PNG-Inhalt öffnet sich zwar überall, aber
   * beim Hochladen stünde der falsche Medientyp daran, und jede Prüfung des Inhalts gegen die
   * Endung schlüge fehl. Auf einem Beleg wiegt das schwerer als ein grösseres Bild.
   */
  format: 'jpg' | 'png';
}

export interface ScreenshotPlugin {
  capture(optionen?: ShotOptionen): Promise<ShotResult>;
}

/**
 * Im Browser über `getDisplayMedia` — s. `platform/screenshotWeb.ts`.
 *
 * ⚠️ **Hier stand bis zum 2026-09-22 „im Browser gibt es keinen Abzug".** Es gibt ihn: der
 * Prüfer gibt den Tab einmal frei, und der Strom wird über den ganzen Durchlauf gehalten.
 *
 * ⚠️ **Ein Fehler bleibt ein Fehler.** Scheitert es — keine Freigabe, kein Bild —, wird
 * geworfen und nichts Leeres zurückgegeben: ein leeres Bild wanderte sonst als vermeintlicher
 * Bogenabzug in die Ablage, und niemand merkte es, bis jemand die Datei öffnet. Die
 * Aufrufstelle fängt und protokolliert (`features/bogen/bogenShot.ts`).
 */
export class ScreenshotWeb extends WebPlugin implements ScreenshotPlugin {
  private readonly halter: Schirmhalter;

  constructor(teile: Partial<SchirmTeile> = {}) {
    super();
    this.halter = new Schirmhalter(baueSchirmTeile(teile));
    webHalter = this.halter;
  }

  async capture(optionen: ShotOptionen = {}): Promise<ShotResult> {
    return this.halter.abzug(abzugsOptionen(optionen));
  }
}

/**
 * Am Desktop nimmt der Hauptprozess das Bild — `webContents.capturePage()`.
 *
 * ⭐ **Kein Freigabedialog, kein falscher Bereich.** Der Browserweg verlangt einmal je
 * Sitzung eine Freigabe durch den Prüfer, und wählt er „ganzer Bildschirm" statt „dieser
 * Tab", steht die Adressleiste auf dem Bogenabzug. Hier wird genau das Fenster aufgenommen,
 * ohne dass jemand etwas anklickt.
 *
 * ⚠️ **Ein Fehler bleibt ein Fehler** — wie im Browserweg: lieber geworfen als ein leeres
 * Bild, das als vermeintlicher Bogenabzug in der Ablage landet. Der Hauptprozess prüft die
 * Masse und meldet einen leeren Abzug selbst.
 */
export class ScreenshotDesktop extends WebPlugin implements ScreenshotPlugin {
  async capture(optionen: ShotOptionen = {}): Promise<ShotResult> {
    const bruecke = desktopBruecke();
    if (!bruecke) throw new Error('Kein Desktop-Gleis — die Brücke fehlt.');
    return bruecke.abzug(abzugsOptionen(optionen));
  }
}

/**
 * ⚠️ **Die Wahl fällt bei jedem Aufruf, nicht beim Laden.** `registerPlugin` ruft diese
 * Fabrik erst, wenn das Plugin zum ersten Mal gebraucht wird; stünde die Entscheidung
 * stattdessen im Modulkopf, hinge sie davon ab, ob die Brücke zu diesem Zeitpunkt schon
 * dastand (s. `platform/gleis.ts`).
 */
let webHalter: Schirmhalter | null = null;

/**
 * Hält der Browserweg gerade eine Freigabe? Relays `SCREENSHOT` darf im Browser nie nach einer
 * fragen (Entscheid des Nutzers, 2026-10-22) — ohne sie meldet `server/bildschirmfoto.ts` `failed`.
 */
export const browserHatFreigabe = (): boolean => webHalter?.hatFreigabe() ?? false;

export const Screenshot = registerPlugin<ScreenshotPlugin>('Screenshot', {
  web: () => (istDesktop() ? new ScreenshotDesktop() : new ScreenshotWeb()),
});

/**
 * Der Abzug, wie ihn die App nimmt — **immer mit Angaben**.
 *
 * ⚠️ **Nicht bequemer, sondern sicherer.** Auf dem iPad nimmt das Swift-Plugin Qualität und
 * Skalierung über die Brücke entgegen und weist einen Aufruf **ohne** sie ab; ein Vorgabewert
 * in Swift wäre eine zweite Wahrheit, die beim nächsten Nachziehen stehen bleibt. Ein nacktes
 * `Screenshot.capture()` fiele damit erst am Gerät auf — nicht im Browser, nicht am Desktop
 * und in keinem Test. Diese Tür lässt das Vergessen nicht zu.
 */
export const ziehAbzug = (ueber: ShotOptionen = {}): Promise<ShotResult> =>
  Screenshot.capture(abzugsOptionen(ueber));
